// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/mentor/hooks/useMentor.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mentorService } from '../services/mentorService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { CriarConversaRequest } from '../types';

const KEYS = {
  conversas: (idEmpresa: number) => ['mentor', 'conversas', idEmpresa] as const,
  conversa:  (id: number)        => ['mentor', 'conversa',  id]        as const,
  stats:     (idEmpresa: number) => ['mentor', 'stats',     idEmpresa] as const,
};

export const useConversas = (idEmpresa: number) =>
  useQuery({
    queryKey: KEYS.conversas(idEmpresa),
    queryFn:  () => mentorService.listarConversas(idEmpresa),
    enabled:  idEmpresa > 0,
    staleTime: 30_000,
  });

export const useConversaDetalhe = (id: number) =>
  useQuery({
    queryKey: KEYS.conversa(id),
    queryFn:  () => mentorService.buscarConversa(id),
    enabled:  id > 0,
  });

export const useEstatisticasIA = (idEmpresa: number) =>
  useQuery({
    queryKey: KEYS.stats(idEmpresa),
    queryFn:  () => mentorService.obterEstatisticas(idEmpresa),
    enabled:  idEmpresa > 0,
    staleTime: 60_000,
  });

export const useCriarConversa = (idEmpresa: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CriarConversaRequest) => mentorService.criarConversa(req),
    onSuccess:  () => qc.invalidateQueries({ queryKey: KEYS.conversas(idEmpresa) }),
    onError:    (e) => toast.error(getApiErrorMessage(e)),
  });
};

export const useArquivarConversa = (idEmpresa: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => mentorService.arquivarConversa(id),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: KEYS.conversas(idEmpresa) });
      toast.success('Conversa arquivada');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
};
